import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditoriaTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, audit } = userData;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Get unique values for filters
  const uniqueActions = useMemo(() => {
    if (!audit) return [];
    return [...new Set(audit.map(a => a.action))].sort();
  }, [audit]);

  const uniqueResources = useMemo(() => {
    if (!audit) return [];
    return [...new Set(audit.map(a => a.resource_type).filter(Boolean))].sort();
  }, [audit]);

  const uniqueAdmins = useMemo(() => {
    if (!audit) return [];
    return [...new Set(audit.map(a => a.admin_email).filter(Boolean))].sort();
  }, [audit]);

  // Filter and search logs
  const filteredLogs = useMemo(() => {
    if (!audit) return [];
    
    return audit.filter(log => {
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const matchesSearch = 
          log.action?.toLowerCase().includes(search) ||
          log.resource_type?.toLowerCase().includes(search) ||
          log.resource_id?.toLowerCase().includes(search) ||
          log.admin_email?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Action filter
      if (filterAction && log.action !== filterAction) return false;

      // Resource filter
      if (filterResource && log.resource_type !== filterResource) return false;

      // Admin filter
      if (filterAdmin && log.admin_email !== filterAdmin) return false;

      // Date filters
      if (dateFrom && new Date(log.timestamp) < new Date(dateFrom)) return false;
      if (dateTo && new Date(log.timestamp) > new Date(dateTo + 'T23:59:59')) return false;

      return true;
    });
  }, [audit, searchQuery, filterAction, filterResource, filterAdmin, dateFrom, dateTo]);

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExport = async (format) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users/${user.id}/audit/export`,
        {
          params: { format },
          headers: { Authorization: `Bearer ${getToken()}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_${user.id}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al exportar logs');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterAction('');
    setFilterResource('');
    setFilterAdmin('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const getActionIcon = (action) => {
    const icons = {
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'scholarship_applied': '🎓',
      'payment_completed': '💳',
      'payment_link_generated': '🔗',
      'activated': '🚀',
      'suspended': '⚠️',
      'community_grant': '👥',
      'community_revoke': '❌',
      'certificate_issued': '🏆',
      'attendance_marked': '✅',
      'project_updated': '📝',
      'manual_payment_registered': '💰',
    };
    return icons[action] || '📌';
  };

  const getActionColor = (action) => {
    if (action?.includes('delete') || action?.includes('revoke')) return 'text-red-400';
    if (action?.includes('create') || action?.includes('grant')) return 'text-green-400';
    if (action?.includes('update')) return 'text-blue-400';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Total Logs</div>
          <div className="text-2xl font-bold text-white">{audit?.length || 0}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Filtrados</div>
          <div className="text-2xl font-bold text-[#D4AF37]">{filteredLogs.length}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Admins Únicos</div>
          <div className="text-2xl font-bold text-white">{uniqueAdmins.length}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Acciones Únicas</div>
          <div className="text-2xl font-bold text-white">{uniqueActions.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Filtros y Búsqueda</h4>
          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600"
            >
              Limpiar Filtros
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-sm hover:bg-green-500/30"
            >
              📥 CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded text-sm hover:bg-blue-500/30"
            >
              📥 JSON
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Buscar en logs (acción, recurso, ID, admin)..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Action filter */}
          <div>
            <select
              value={filterAction}
              onChange={(e) => {
                setFilterAction(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="">Todas las acciones</option>
              {uniqueActions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          {/* Resource filter */}
          <div>
            <select
              value={filterResource}
              onChange={(e) => {
                setFilterResource(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="">Todos los recursos</option>
              {uniqueResources.map(resource => (
                <option key={resource} value={resource}>{resource}</option>
              ))}
            </select>
          </div>

          {/* Admin filter */}
          <div>
            <select
              value={filterAdmin}
              onChange={(e) => {
                setFilterAdmin(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="">Todos los admins</option>
              {uniqueAdmins.map(admin => (
                <option key={admin} value={admin}>{admin}</option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              placeholder="Desde"
            />
          </div>

          {/* Date to */}
          <div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              placeholder="Hasta"
            />
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-black/30 rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Acción</th>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Recurso</th>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">ID Recurso</th>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">Admin</th>
                <th className="px-4 py-3 text-left text-xs text-gray-400 uppercase">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a4d5c]/30">
              {paginatedLogs.length > 0 ? (
                paginatedLogs.map((log, index) => (
                  <tr key={index} className="hover:bg-[#1a4d5c]/10">
                    <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getActionIcon(log.action)}</span>
                        <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {log.resource_type || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 font-mono text-xs">
                      {log.resource_id?.substring(0, 20) || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">
                      {log.admin_email || 'System'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                    No se encontraron logs con los filtros aplicados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-[#1a4d5c]/30 flex items-center justify-between bg-black/30">
            <div className="text-sm text-gray-400">
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredLogs.length)} de {filteredLogs.length}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-[#1a4d5c]/30 text-white rounded disabled:opacity-50 hover:bg-[#1a4d5c]/50"
              >
                ← Anterior
              </button>
              <span className="px-3 py-1 text-white">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-[#1a4d5c]/30 text-white rounded disabled:opacity-50 hover:bg-[#1a4d5c]/50"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}